import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import ChatPanel, { SorenMark } from "@/components/ChatPanel";
import BookCall from "@/components/BookCall";
import { ArrowRight, ShieldCheck, MessageCircle } from "lucide-react";

// What he's actually good for — deliberately modest, so nobody expects an oracle.
const KNOWS = [
  ["Setup, in the right order", "TP Wallet, BOT Chain, USDT, the invitation code — and what to do when a step fails."],
  ["What the numbers mean", "Mining, staking terms, VIP levels, and why CA on a screen isn't money yet."],
  ["The risks, straight", "What can go wrong, what we never promise, and why your first $100 is a dress rehearsal."],
  ["Where to click", "Which page of this dashboard answers the thing you're stuck on."],
];

export const MeetSoren = () => {
  const { user } = useAuth();

  return (
    <section className="px-6 md:px-10 mt-20" id="soren">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[0.95fr_1.05fr] gap-8 lg:gap-12 items-start">
        <div className="fade-up" data-testid="meet-soren">
          <div className="chip chip-blue mb-5 inline-flex items-center gap-1.5">
            <MessageCircle size={12} /> Meet Søren
          </div>
          <div className="flex items-center gap-4 mb-4">
            <SorenMark size={64} />
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
              Say hello to <span className="gradient-text">Søren</span>.
            </h2>
          </div>
          <p className="text-ink-2 leading-relaxed mb-4">
            Søren is the little bot in our logo, brought to life as the assistant inside BotChain CP.
            He&rsquo;s read the CaryPact one-pager, the whole 12-step setup and every formula this
            dashboard uses, so he can answer the basic questions at 2am without waiting on a human.
          </p>
          <p className="text-sm text-ink-2 leading-relaxed mb-6">
            He&rsquo;s a bot and he&rsquo;ll say so. He won&rsquo;t guess at a number he can&rsquo;t see, and
            when a question needs a person he passes you to the team.{" "}
            <span className="text-ink-3">(It&rsquo;s spelled with the Danish ø and said &ldquo;SUR-en&rdquo;.)</span>
          </p>

          <div className="space-y-2.5 mb-6">
            {KNOWS.map(([t, d]) => (
              <div key={t} className="glass-sm px-4 py-3" data-testid={`soren-knows-${t.slice(0, 10).replace(/\W+/g, "-").toLowerCase()}`}>
                <div className="font-display text-sm font-semibold tracking-tight">{t}</div>
                <div className="text-xs text-ink-2 mt-0.5 leading-snug">{d}</div>
              </div>
            ))}
          </div>

          <div className="glass p-5 flex items-start gap-3" style={{ borderRadius: 16 }}>
            <ShieldCheck size={20} style={{ color: "#00C853" }} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-sm mb-1">What he will never do</div>
              <div className="text-xs text-ink-2 leading-relaxed">
                Ask for your seed phrase, touch your money, or promise you a return. Signed out he
                can&rsquo;t see any account — log in and he reads your own wallets and figures.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            {user ? (
              <Link to="/ask" className="btn-primary" data-testid="soren-open-full">
                Open the full chat <ArrowRight size={16} />
              </Link>
            ) : (
              <>
                <Link to="/login" className="btn-primary" data-testid="soren-section-login">
                  Log in for the full Søren <ArrowRight size={16} />
                </Link>
                <a href="#signup" className="btn-secondary" data-testid="soren-section-signup">Request a walkthrough</a>
              </>
            )}
            <BookCall className="btn-ghost" label="Talk to a human" />
          </div>
        </div>

        <div className="glass-strong overflow-hidden fade-up" style={{ borderRadius: 22, height: 560 }} data-testid="soren-chat-wrap">
          <ChatPanel guest={!user} />
        </div>
      </div>
    </section>
  );
};

export default MeetSoren;
